import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";
import {
  assignLocationAdminToLocation,
  createLocation,
  deleteLocation,
  getLocations,
  removeLocationAdminToLocation,
  updateLocation,
} from "data/server_apis/inventoryService";

const locationAdapter = createEntityAdapter({
  selectId: (location) => location.id,
});

export const getLocationsThunk = createAsyncThunk(
  "inv/getLocations",
  async (_, { rejectWithValue }) => {
    try {
      return (await getLocations()).data;
    } catch (err) {
      console.log(err);
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const createLocationThunk = createAsyncThunk(
  "inv/createLocation",
  async (data, { rejectWithValue }) => {
    try {
      return (await createLocation(data)).data;
    } catch (err) {
      console.log(err);
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const updateLocationThunk = createAsyncThunk(
  "inv/updateLocation",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return (await updateLocation(id, data)).data;
    } catch (err) {
      console.log(err);
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const deleteLocationThunk = createAsyncThunk(
  "inv/deleteLocation",
  async (id, { rejectWithValue }) => {
    try {
      return (await deleteLocation(id)).data;
    } catch (err) {
      console.log(err);
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const assignLocationAdminToLocationThunk = createAsyncThunk(
  "inv/assignLocationAdminToLocation",
  async (data, { rejectWithValue }) => {
    try {
      return (
        await assignLocationAdminToLocation({
          ...data,
          location_admin: data.location_admin.id,
        })
      ).data;
    } catch (err) {
      console.log(err);
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);
export const removeLocationAdminToLocationThunk = createAsyncThunk(
  "inv/removeLocationAdminToLocation",
  async (data, { rejectWithValue }) => {
    try {
      return (await removeLocationAdminToLocation(data)).data;
    } catch (err) {
      console.log(err);
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

const locationSlice = createSlice({
  name: "location",
  initialState: locationAdapter.getInitialState({
    loading: {},
    error: {},
    errorMessage: {},
    success: {},
  }),
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createLocationThunk.pending, (state, action) => {
        state.loading = { create: true };
        state.error = {};
        state.success = {};
        state.errorMessage = {};
      })
      .addCase(createLocationThunk.rejected, (state, action) => {
        state.loading = {};
        state.error = { create: true };
        state.success = {};
        state.errorMessage = { create: action.payload?.message };
      })
      .addCase(createLocationThunk.fulfilled, (state, action) => {
        state.loading = {};
        state.error = {};
        state.success = { create: true };
        state.errorMessage = {};
        locationAdapter.addOne(state, action.payload);
      });

    builder
      .addCase(assignLocationAdminToLocationThunk.pending, (state, action) => {
        state.loading = { assignAdmin: action.meta.arg.location_admin };
        state.error = {};
        state.success = {};
        state.errorMessage = {};
      })
      .addCase(assignLocationAdminToLocationThunk.rejected, (state, action) => {
        state.loading = {};
        state.error = { assignAdmin: action.meta.arg.location_admin };
        state.success = {};
        state.errorMessage = { assignAdmin: action.payload?.message };
      })
      .addCase(
        assignLocationAdminToLocationThunk.fulfilled,
        (state, action) => {
          state.loading = {};
          state.error = {};
          state.success = { assignAdmin: action.meta.arg.location_admin };
          state.errorMessage = {};
          locationAdapter.updateOne(state, {
            id: action.meta.arg.location,
            changes: { location_admin: action.meta.arg.location_admin },
          });
        }
      );

    builder
      .addCase(removeLocationAdminToLocationThunk.pending, (state, action) => {
        state.loading = { removeAdmin: action.meta.arg.location_admin };
        state.error = {};
        state.success = {};
        state.errorMessage = {};
      })
      .addCase(removeLocationAdminToLocationThunk.rejected, (state, action) => {
        state.loading = {};
        state.error = { removeAdmin: action.meta.arg.location_admin };
        state.success = {};
        state.errorMessage = { removeAdmin: action.payload?.message };
      })
      .addCase(
        removeLocationAdminToLocationThunk.fulfilled,
        (state, action) => {
          state.loading = {};
          state.error = {};
          state.success = { removeAdmin: action.meta.arg.location_admin };
          state.errorMessage = {};
          locationAdapter.updateOne(state, {
            id: action.meta.arg.location,
            changes: { location_admin: null },
          });
        }
      );

    builder
      .addCase(updateLocationThunk.pending, (state, action) => {
        state.loading = { update: true };
        state.error = {};
        state.success = {};
        state.errorMessage = {};
      })
      .addCase(updateLocationThunk.rejected, (state, action) => {
        state.loading = {};
        state.error = { update: true };
        state.success = {};
        state.errorMessage = { update: action.payload?.message };
      })
      .addCase(updateLocationThunk.fulfilled, (state, action) => {
        state.loading = {};
        state.error = {};
        state.success = { update: true };
        state.errorMessage = {};
        locationAdapter.updateOne(state, {
          id: action.meta.arg.id,
          changes: action.payload,
        });
      });
    builder
      .addCase(getLocationsThunk.pending, (state, action) => {
        state.loading = { list: true };
        state.error = {};
        state.success = {};
        state.errorMessage = {};
      })
      .addCase(getLocationsThunk.rejected, (state, action) => {
        state.loading = {};
        state.error = { list: true };
        state.success = {};
        state.errorMessage = { list: action.payload?.message };
      })
      .addCase(getLocationsThunk.fulfilled, (state, action) => {
        state.loading = {};
        state.error = {};
        state.success = { list: true };
        state.errorMessage = {};
        locationAdapter.setAll(state, action.payload);
      });

    builder
      .addCase(deleteLocationThunk.pending, (state, action) => {
        state.loading = { delete: action.meta.arg };
        state.error = {};
        state.success = {};
        state.errorMessage = {};
      })
      .addCase(deleteLocationThunk.rejected, (state, action) => {
        state.loading = {};
        state.error = { delete: true };
        state.success = {};
        state.errorMessage = { delete: action.payload?.message };
      })
      .addCase(deleteLocationThunk.fulfilled, (state, action) => {
        state.loading = {};
        state.error = {};
        state.success = { delete: true };
        state.errorMessage = {};
        locationAdapter.removeOne(state, action.meta.arg);
      });
  },
});

export const locationSelector = locationAdapter.getSelectors(
  (state) => state.location
);

export const {} = locationSlice.actions;

export default locationSlice.reducer;
